import React, { useRef, useState } from 'react';
import Button from '../components/Button';
import Card from '../components/Card';
import { apiBase, portalBase } from '../lib/api';
import { useDonationsAvailable } from '../hooks/useDonationsAvailable';
// Generated mirror of backend/src/services/processingFee.ts — the same function the checkout
// route uses to price the fee line item, so the number quoted here is the number charged.
// Written by scripts/sync-processing-fee.mjs; drift fails scripts/__tests__/sync-processing-fee.test.mjs.
import { calculateProcessingFee } from '../lib/processingFee';

// No Stripe key here on purpose: the backend creates the Checkout Session with the
// SECRET key and returns a hosted `session.url` that we redirect to (see handleSubmit).
// The browser never talks to Stripe's API directly, so it needs no publishable key.

interface DonationFormProps {
  onSuccess: () => void;
}

const DonationForm: React.FC<DonationFormProps> = ({ onSuccess }) => {
  const [amount, setAmount] = useState<string>('50');
  const [customAmount, setCustomAmount] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [coverFees, setCoverFees] = useState<boolean>(true);
  const [recurring, setRecurring] = useState<boolean>(false);
  const [frequency, setFrequency] = useState<string>('MONTHLY');
  const [advanced, setAdvanced] = useState<boolean>(false);
  const [recurringRule, setRecurringRule] = useState<string>('');
  const [donorInfo, setDonorInfo] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    zipCode: ''
  });
  // Donor anonymity choice: NAMED (default) | ANONYMOUS | FULLY_ANONYMOUS.
  const [anonymity, setAnonymity] = useState<string>('NAMED');
  // Review step: the receipt, the portal sign-in code and every recurring receipt go to this
  // address, and a typo in it can't be fixed by email — so the donor reads it back once before
  // Stripe. Editing the field closes the review.
  const [confirmingEmail, setConfirmingEmail] = useState(false);
  const emailInput = useRef<HTMLInputElement>(null);

  const predefinedAmounts = ['10', '50', '100', '250'];

  const getDonationAmount = (): number => {
    return parseFloat(customAmount || amount);
  };

  const getProcessingFee = (): number => {
    return coverFees ? calculateProcessingFee(getDonationAmount()) : 0;
  };

  const getTotalAmount = (): number => {
    return getDonationAmount() + getProcessingFee();
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    // Validate required fields (a fully-anonymous gift collects no identity)
    if (anonymity !== 'FULLY_ANONYMOUS' && (!donorInfo.firstName || !donorInfo.lastName || !donorInfo.email)) {
      alert('Please fill in your name and email address.');
      return;
    }

    // A fully-anonymous gift has no address to check.
    if (anonymity === 'FULLY_ANONYMOUS') {
      startCheckout();
    } else {
      setConfirmingEmail(true);
    }
  };

  const editEmail = () => {
    setConfirmingEmail(false);
    emailInput.current?.focus();
    emailInput.current?.select();
  };

  const startCheckout = async () => {
    const donationAmount = customAmount || amount;
    const fullyAnonymous = anonymity === 'FULLY_ANONYMOUS';

    setIsProcessing(true);

    try {
      // Call our donation API to create checkout session
      const response = await fetch(`${apiBase}/api/donations/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount: parseFloat(donationAmount),
          // A fully-anonymous gift sends no donor identity. The backend's donorInfo.name is a
          // single field (Stripe metadata, the DAL's donorName, the receipt PDF all store one
          // string) — first/last are collected as two fields and joined only for the wire.
          donorInfo: fullyAnonymous ? {} : {
            name: `${donorInfo.firstName} ${donorInfo.lastName}`.trim(),
            email: donorInfo.email,
            phone: donorInfo.phone,
            address: donorInfo.address,
            city: donorInfo.city,
            zipCode: donorInfo.zipCode
          },
          anonymity,
          coverProcessingFees: coverFees,
          recurring,
          ...(recurring
            ? (advanced && recurringRule.trim()
                ? { recurringRule: recurringRule.trim() }
                : { recurringInterval: frequency })
            : {}),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create checkout session');
      }

      const { url } = await response.json();

      // Redirect to Stripe Checkout
      window.location.href = url;

    } catch (err: any) {
      console.error('Error:', err);
      alert(`Error: ${err?.message || 'An error occurred while processing your donation.'}`);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Amount Selection */}
      <div>
        <h3 className="text-lg font-bold mb-4 text-gray-800">Donation Amount</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          {predefinedAmounts.map((presetAmount) => (
            <button
              key={presetAmount}
              type="button"
              onClick={() => {
                setAmount(presetAmount);
                setCustomAmount('');
              }}
              className={`p-3 rounded-lg border-2 transition-colors ${
                amount === presetAmount && !customAmount
                  ? 'border-[var(--primary)] bg-[var(--primary)] text-white'
                  : 'border-gray-300 hover:border-[var(--primary)]'
              }`}
            >
              ${presetAmount}
            </button>
          ))}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Custom Amount ($)
          </label>
          <input
            type="number"
            min="1"
            value={customAmount}
            onChange={(e) => {
              setCustomAmount(e.target.value);
              setAmount('');
            }}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--primary)] focus:border-transparent"
            placeholder="Enter custom amount"
          />
        </div>
      </div>

      {/* Recurring gift */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <input
            type="checkbox"
            id="recurring"
            checked={recurring}
            onChange={(e) => setRecurring(e.target.checked)}
            className="mt-1 h-4 w-4 text-[var(--primary)] focus:ring-[var(--primary)] border-gray-300 rounded"
          />
          <div className="flex-1">
            <label htmlFor="recurring" className="text-sm font-medium text-gray-900 cursor-pointer">
              Make this a recurring gift
            </label>
            <p className="text-xs text-gray-600 mt-1">
              Give automatically on a schedule. We securely save your card and you can change or cancel anytime.
            </p>
          </div>
        </div>

        {recurring && (
          <div className="mt-4 pt-4 border-t border-gray-200 space-y-3">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {[['MONTHLY', 'Monthly'], ['WEEKLY', 'Weekly'], ['ANNUAL', 'Annual'], ['DAILY', 'Daily']].map(([val, label]) => (
                <button
                  key={val}
                  type="button"
                  onClick={() => { setFrequency(val); setAdvanced(false); }}
                  className={`px-3 py-2 rounded-lg border-2 text-sm transition-colors ${
                    !advanced && frequency === val
                      ? 'border-[var(--primary)] bg-[var(--primary)] text-white'
                      : 'border-gray-300 hover:border-[var(--primary)]'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            <button
              type="button"
              onClick={() => setAdvanced(!advanced)}
              className="text-sm text-[var(--primary)] font-medium hover:underline"
            >
              {advanced ? '▾ Advanced schedule' : '▸ Advanced schedule (custom)'}
            </button>

            {advanced && (
              <div className="space-y-2">
                <input
                  type="text"
                  value={recurringRule}
                  onChange={(e) => setRecurringRule(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--primary)] focus:border-transparent font-mono text-sm"
                  placeholder="FREQ=WEEKLY;BYDAY=TH"
                />
                <div className="flex flex-wrap gap-2">
                  {[['Every Thursday', 'FREQ=WEEKLY;BYDAY=TH'], ['1st of month', 'FREQ=MONTHLY;BYMONTHDAY=1'], ['Every 2 weeks', 'FREQ=WEEKLY;INTERVAL=2']].map(([label, rule]) => (
                    <button
                      key={rule}
                      type="button"
                      onClick={() => setRecurringRule(rule)}
                      className="px-2.5 py-1 rounded-full border border-gray-300 text-xs text-gray-700 hover:border-[var(--primary)] hover:text-[var(--primary)]"
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-gray-500">
                  Uses the iCalendar <span className="font-mono">RRULE</span> format for custom repeats.
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Processing Fees Toggle */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <input
            type="checkbox"
            id="coverFees"
            checked={coverFees}
            onChange={(e) => setCoverFees(e.target.checked)}
            className="mt-1 h-4 w-4 text-[var(--primary)] focus:ring-[var(--primary)] border-gray-300 rounded"
          />
          <div className="flex-1">
            <label htmlFor="coverFees" className="text-sm font-medium text-gray-900 cursor-pointer">
              Help cover processing fees (+${getProcessingFee().toFixed(2)})
            </label>
            <p className="text-xs text-gray-600 mt-1">
              Credit card processing fees are ${getProcessingFee().toFixed(2)}. Covering these fees helps more of your donation go directly to pets in need.
            </p>
          </div>
        </div>
        <div className="mt-3 pt-3 border-t border-gray-200 flex justify-between items-center text-sm">
          <span className="text-gray-700">
            Donation: ${getDonationAmount().toFixed(2)}
            {coverFees && ` + Processing: $${getProcessingFee().toFixed(2)}`}
          </span>
          <span className="font-medium text-gray-900">
            Total: ${getTotalAmount().toFixed(2)}
          </span>
        </div>
      </div>

      {/* Donor Information + anonymity choice */}
      <div>
        <h3 className="text-lg font-bold mb-4 text-gray-800">Donor Information</h3>

        {/* How the donor's identity is treated */}
        <div className="mb-4 space-y-2">
          {[
            { value: 'NAMED', label: 'Show my name', hint: 'Recognized publicly; you receive a tax receipt.' },
            { value: 'ANONYMOUS', label: 'Give anonymously', hint: 'Your name is hidden from the public and recipient organizations; you still receive a tax receipt.' },
            { value: 'FULLY_ANONYMOUS', label: "Fully anonymous — don't collect my info", hint: 'We collect no personal information for this gift.' },
          ].map((opt) => (
            <label key={opt.value} className="flex items-start gap-3 cursor-pointer">
              <input
                type="radio"
                name="anonymity"
                value={opt.value}
                checked={anonymity === opt.value}
                onChange={(e) => setAnonymity(e.target.value)}
                className="mt-1"
              />
              <span>
                <span className="font-medium text-gray-800">{opt.label}</span>
                <span className="block text-sm text-gray-500">{opt.hint}</span>
              </span>
            </label>
          ))}
        </div>

        {anonymity === 'FULLY_ANONYMOUS' ? (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-amber-800 text-sm">
            <strong>Heads up:</strong> a fully-anonymous gift is untracked — we can't issue a tax
            receipt and it won't appear in any donation history. See our{' '}
            <a href={`${portalBase}/terms`} target="_blank" rel="noopener" className="underline font-medium">Terms of Service</a>.
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            <input
              type="text"
              required
              value={donorInfo.firstName}
              onChange={(e) => setDonorInfo({ ...donorInfo, firstName: e.target.value })}
              className="px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--primary)] focus:border-transparent"
              placeholder="First Name"
              autoComplete="given-name"
            />
            <input
              type="text"
              required
              value={donorInfo.lastName}
              onChange={(e) => setDonorInfo({ ...donorInfo, lastName: e.target.value })}
              className="px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--primary)] focus:border-transparent"
              placeholder="Last Name"
              autoComplete="family-name"
            />
            <input
              ref={emailInput}
              type="email"
              required
              value={donorInfo.email}
              onChange={(e) => {
                setDonorInfo({ ...donorInfo, email: e.target.value });
                setConfirmingEmail(false);
              }}
              className="px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--primary)] focus:border-transparent"
              placeholder="Email Address"
              autoComplete="email"
            />
            <input
              type="tel"
              value={donorInfo.phone}
              onChange={(e) => setDonorInfo({ ...donorInfo, phone: e.target.value })}
              className="px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--primary)] focus:border-transparent"
              placeholder="Phone (optional — manage gifts by text)"
            />
            <input
              type="text"
              value={donorInfo.address}
              onChange={(e) => setDonorInfo({ ...donorInfo, address: e.target.value })}
              className="px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--primary)] focus:border-transparent"
              placeholder="Address (Optional)"
            />
            <div className="grid grid-cols-2 gap-4">
              <input
                type="text"
                value={donorInfo.city}
                onChange={(e) => setDonorInfo({ ...donorInfo, city: e.target.value })}
                className="px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--primary)] focus:border-transparent"
                placeholder="City"
              />
              <input
                type="text"
                value={donorInfo.zipCode}
                onChange={(e) => setDonorInfo({ ...donorInfo, zipCode: e.target.value })}
                className="px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--primary)] focus:border-transparent"
                placeholder="ZIP Code"
              />
            </div>
          </div>
        )}
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-blue-800 text-sm">
          <strong>Secure Payment:</strong> You'll be redirected to Stripe's secure payment page to complete your donation with your credit card information.
        </p>
      </div>

      {confirmingEmail && anonymity !== 'FULLY_ANONYMOUS' ? (
        <div className="rounded-lg border-2 border-[var(--primary)] p-4 text-center" role="alertdialog" aria-labelledby="confirm-email-title">
          <p id="confirm-email-title" className="text-sm text-gray-700 mb-1">
            Your receipt{recurring ? ', every recurring receipt,' : ''} and your portal sign-in code go to:
          </p>
          <p className="text-xl font-bold text-gray-900 break-all mb-1">{donorInfo.email}</p>
          <p className="text-sm text-gray-600 mb-4">Is this exactly right?</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Button type="button" variant="primary" size="lg" className="w-full" disabled={isProcessing} onClick={startCheckout}>
              {isProcessing ? 'Redirecting to Stripe...' : 'Yes, continue to payment'}
            </Button>
            <Button type="button" variant="secondary" size="lg" className="w-full" disabled={isProcessing} onClick={editEmail}>
              Edit email
            </Button>
          </div>
        </div>
      ) : (
        <Button
          type="submit"
          variant="primary"
          size="lg"
          className="w-full"
          disabled={isProcessing}
        >
          {isProcessing
            ? 'Redirecting to Stripe...'
            : recurring
              ? `Start recurring gift · $${getTotalAmount().toFixed(2)}${advanced && recurringRule.trim() ? '' : '/' + ({ MONTHLY: 'mo', WEEKLY: 'wk', ANNUAL: 'yr', DAILY: 'day' }[frequency] || 'mo')}`
              : `Proceed to Payment - $${getTotalAmount().toFixed(2)}`
          }
        </Button>
      )}

      <p className="text-sm text-gray-600 text-center">
        Your donation is secure and helps provide essential veterinary care for pets in need.
      </p>
    </form>
  );
};

const DonatePage: React.FC = () => {
  const [donationComplete, setDonationComplete] = useState(false);
  const donationsStatus = useDonationsAvailable();

  if (donationComplete) {
    return (
      <div className="py-20">
        <div className="container mx-auto px-6 text-center">
          <Card className="p-8 max-w-2xl mx-auto">
            <div className="text-6xl mb-6">🎉</div>
            <h1 className="text-3xl font-bold mb-4 text-gray-800">Thank You!</h1>
            <p className="text-xl text-gray-700 mb-6">
              Your generous donation will help us provide essential veterinary care for pets in need.
            </p>
            <Button onClick={() => setDonationComplete(false)}>
              Make Another Donation
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="py-20">
      <div className="container mx-auto px-6">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4 text-gray-800">Help Create Pawsitive Futures</h1>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto">
            Your donation directly funds essential veterinary care for pets in need and supports our mission to reduce pet overpopulation.
          </p>
        </div>

        <div className="max-w-2xl mx-auto">
          <Card className="p-8">
            {/* Deep links reach this page directly, so gate the form too — not just the CTAs. */}
            {donationsStatus === 'available' ? (
              <DonationForm onSuccess={() => setDonationComplete(true)} />
            ) : (
              <div className="text-center py-6">
                <h2 className="text-2xl font-bold mb-3 text-gray-800">
                  {donationsStatus === 'checking' ? 'Checking availability…' : 'Donations Coming Soon'}
                </h2>
                {donationsStatus === 'unavailable' && (
                  <p className="text-gray-700">
                    We are not able to accept donations just yet. Please check back shortly — this page
                    opens automatically as soon as we are ready.
                  </p>
                )}
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};

export default DonatePage;